import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import App from "./App.vue";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import router from "./router";
import axios from "axios";
import clickOutside from "./directives/clickOutside";
import Loader from "./services/LoaderOverlay";
import { useJwt } from "@vueuse/integrations/useJwt";
// import main css
// import './css/main.css'
import "/index.css";
import store from "./store";
import "flowbite";
import "flowbite/dist/flowbite.js";
import { showGlobalError } from "./services/ErrorService";
axios.defaults.baseURL = process.env.VUE_APP_APIURL;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    try {
      const { payload } = useJwt(token);
      const exp = payload.value.exp * 1000;
      // console.log(exp);
      // console.log(Date.now());
      if (Date.now() >= exp) {
        console.warn("Token expired, logging out...");
        handleLogout();
        return Promise.reject(new Error("Token expired"));
      }
    } catch (error) {
      console.error("Invalid token detected, logging out...");
      handleLogout();
      return Promise.reject(new Error("Invalid token"));
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.log("Axios Response Interceptor triggered:");
    console.log(error.response);
    if (error.response && error.response.data) {
      if (error.response.status == 401) {
        const currentRoute = router.currentRoute?.value;
        if (!localStorage.getItem("redirectAfterLogin") && currentRoute) {
          localStorage.setItem(
            "redirectAfterLogin",
            JSON.stringify({
              path: currentRoute.fullPath,
            }),
          );
          console.log("Saved redirectAfterLogin:", currentRoute.fullPath);
        }
        localStorage.removeItem("token");
        localStorage.removeItem("darkmode");
        localStorage.removeItem("user_id");
        localStorage.removeItem("internal");
        console.log("Redirecting to login page...");
        showGlobalError(error.response.data?.message || "An error occurred.");
        router.push({ name: "login" });
      } else {
        const rawMessage = error.response.data?.message || "An error occurred.";
        console.log("Backend error message:", rawMessage);
        if (rawMessage.includes("SQLSTATE")) {
          console.warn("Suppressed raw SQL error:", rawMessage);
          showGlobalError("An error occurred.");
        } else {
          showGlobalError(rawMessage);
        }
      }
    }
    return Promise.reject(error);
  },
);

function handleLogout() {
  localStorage.removeItem("token");
  localStorage.removeItem("darkmode");
  localStorage.removeItem("user_id");
  localStorage.removeItem("internal");

  router.push({ name: "login" });
}

async function fetchLogo() {
  try {
    const response = await axios.get("/getlogo");
    const logoName = response.data.logoname || "aim_logo.png";
    store.commit("setLogo", logoName);
    localStorage.setItem("logo", logoName);
  } catch (error) {
    console.error("Error fetching logo:", error);
    store.commit("setLogo", "aim_logo.png");
    localStorage.setItem("logo", "aim_logo.png");
  }
}

fetchLogo();

const app = createApp(App);

if (process.env.VUE_APP_SENTRY_DSN) {
  const env = process.env.VUE_APP_ENV || "development";
  const isProd = env === "production";
  Sentry.init({
    app,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    environment: env,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.browserProfilingIntegration(),
      Sentry.consoleLoggingIntegration({
        levels: ["log", "info", "warn", "error"],
      }),
    ],
    // Performance: traces. Env-driven so it can be tuned per environment
    // without a rebuild; defaults to 10% to keep span volume in budget.
    tracesSampleRate: Number(
      process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
    ),
    // Profiling is sampled FROM traces (so effective rate = traces * profiles).
    profilesSampleRate: Number(
      process.env.VUE_APP_SENTRY_PROFILES_SAMPLE_RATE ?? 0.1,
    ),
    // Structured logs from console.* calls; requires this flag to be on.
    _experiments: { enableLogs: true },
    // Distributed tracing: propagate sentry-trace/baggage headers to our API.
    // Matches same-origin requests and any atms-api.* host.
    tracePropagationTargets: [/^\//, /atms-api\./],
    // Drop Vue component-lifecycle spans — noisy, low signal.
    trackComponents: false,
    ignoreErrors: [/runtime\.sendMessage/i, "Position -1 out of range"],
    // Drop known third-party library cleanup races that fire AFTER our
    // components have unmounted. These are caught by Sentry's setTimeout /
    // requestAnimationFrame auto-instrumentation, so they never reach our
    // Vue errorHandler — they have to be filtered here at the wire.
    beforeSend(event, hint) {
      const ex = hint && hint.originalException;
      if (!ex) return event;
      const msg = (ex.message || "").toString();
      const name = ex.name || "";
      const stack = (ex.stack || "").toString();

      const EDITOR_STACK_FINGERPRINTS = [
        "@toast-ui/editor", // dev / source-mapped builds
        "prosemirror",
        "toastui",
        "ToastMark",
        "updateLineTexts",
        "updateMarkdown",
        "editMarkdown",
        "dispatchTransaction",
        "isInTableCellNode",
        "classifyToolbarItems",
        "HeadingPopupBody",
      ];
      const isEditorStack = EDITOR_STACK_FINGERPRINTS.some((sig) =>
        stack.includes(sig),
      );

      if (
        isEditorStack &&
        ((name === "NotFoundError" && msg.includes("removeChild")) ||
          (name === "TypeError" && msg.includes("slice")) ||
          msg.includes("getAttribute") ||
          msg.includes("Position -1 out of range"))
      ) {
        return null;
      }

      return event;
    },
  });
}

const upstreamErrorHandler = app.config.errorHandler;

const TIER1_TOKENS = ["emitsOptions", "Symbol(_vei)", "Symbol(_vtc)", "'bum'"];

const TIER2_DOM_TOKENS = ["insertBefore", "parentNode", "innerHTML"];

const isFromVuePatch = (info) =>
  typeof info === "string" &&
  (info.includes("render") ||
    info.includes("hook") ||
    info.includes("scheduler") ||
    info.includes("watcher"));

const isVueRuntimeRace = (err, info) => {
  if (!err) return false;
  const msg = err.message ? String(err.message) : "";

  if (err instanceof TypeError && TIER1_TOKENS.some((t) => msg.includes(t))) {
    return true;
  }
  if (
    err instanceof TypeError &&
    isFromVuePatch(info) &&
    TIER2_DOM_TOKENS.some((t) => msg.includes(t))
  ) {
    return true;
  }
  if (
    err.name === "NotFoundError" &&
    isFromVuePatch(info) &&
    msg.includes("removeChild")
  ) {
    return true;
  }
  return false;
};

app.config.errorHandler = (err, instance, info) => {
  if (isVueRuntimeRace(err, info)) {
    console.warn("Suppressed Vue runtime race:", info, err && err.message);
    return;
  }
  if (typeof upstreamErrorHandler === "function") {
    upstreamErrorHandler(err, instance, info);
  } else {
    console.error(err);
  }
};

app.use(Toast);
app.directive("click-outside", clickOutside);
app.use(store);
app.use(router);
app.use(Loader);
app.mount("#app");
